import { z } from 'zod';

// ─── Helper Enums / Unions ──────────────────────────────────────────────────

export const SeveritySchema = z.enum(['low', 'medium', 'high', 'critical']);
export type Severity = z.infer<typeof SeveritySchema>;

export const ObjectionCategorySchema = z.enum([
  'efficacy_doubt',
  'safety_concern',
  'access_barrier',
  'guideline_conflict',
  'experience_conflict',
  'patient_fit',
  'competitive_preference',
  'data_insufficiency',
  'workflow_burden',
  'other',
]);
export type ObjectionCategory = z.infer<typeof ObjectionCategorySchema>;

export const RootCauseSchema = z.enum([
  'clinical_experience',
  'published_evidence',
  'peer_influence',
  'patient_population',
  'formulary_constraints',
  'habit',
  'risk_aversion',
  'other',
]);
export type RootCause = z.infer<typeof RootCauseSchema>;

export const EvidenceTypeSchema = z.enum([
  'rct',
  'real_world',
  'meta_analysis',
  'case_study',
  'expert_opinion',
  'guideline',
  'mechanistic',
  'patient_reported',
]);
export type EvidenceType = z.infer<typeof EvidenceTypeSchema>;

export const ToneSchema = z.enum([
  'authoritative',
  'conversational',
  'empathetic',
  'clinical',
  'urgent',
  'neutral',
  'aspirational',
]);
export type Tone = z.infer<typeof ToneSchema>;

export const BehaviorHorizonSchema = z.enum([
  'immediate',
  'within_week',
  'within_month',
  'within_quarter',
  'unlikely',
]);
export type BehaviorHorizon = z.infer<typeof BehaviorHorizonSchema>;

// ─── Evidence & Score Primitives ────────────────────────────────────────────

export const EvidenceSpanSchema = z.object({
  source: z.enum(['message', 'reaction', 'personaProfile']),
  quote: z.string(),
  impact: z.enum(['positive', 'negative', 'neutral']),
});
export type EvidenceSpan = z.infer<typeof EvidenceSpanSchema>;

export const ScoreSchema = z.object({
  value: z.number().min(0).max(100),
  confidence: z.number().min(0).max(1),
  reasoning: z.string(),
  evidence: z.array(EvidenceSpanSchema),
  personaAttributesUsed: z.array(z.string()),
});
export type Score = z.infer<typeof ScoreSchema>;

// ─── Input Schemas ──────────────────────────────────────────────────────────

export const MessageTestingMessageSchema = z.object({
  id: z.string(),
  value: z.string(),
  category: z.string(),
});
export type MessageTestingMessage = z.infer<typeof MessageTestingMessageSchema>;

export const MessageTestingRequestSchema = z.object({
  segmentIdentifiers: z.array(z.string()).min(1),
  messages: z.array(MessageTestingMessageSchema).min(1),
  brand: z.string(),
  objective: z.string().optional(),
});
export type MessageTestingRequest = z.infer<typeof MessageTestingRequestSchema>;

// ─── Step 1: Reaction Transcript ────────────────────────────────────────────

export const ReactionArcPhaseSchema = z.object({
  phase: z.enum(['opener', 'middle', 'close']),
  sentiment: z.enum(['positive', 'neutral', 'negative', 'mixed', 'measured', 'skeptical', 'cautious']),
  summary: z.string(),
});

export const ReactionTranscriptSchema = z.object({
  messageId: z.string(),
  segmentIdentifier: z.string(),
  transcript: z.string(),
  personaAttributesInvoked: z.array(z.string()),
  reactionArc: z.array(ReactionArcPhaseSchema),
});
export type ReactionTranscript = z.infer<typeof ReactionTranscriptSchema>;

// ─── Step 2: Message Testing Cell ───────────────────────────────────────────

export const ObjectionSchema = z.object({
  text: z.string(),
  category: ObjectionCategorySchema,
  severity: SeveritySchema,
  addressableLive: z.boolean(),
  rootCause: RootCauseSchema,
  suggestedRebuttal: z.string(),
  personaAttribute: z.string(),
  evidenceSpan: EvidenceSpanSchema,
  fingerprint: z.string(),
});

export const MissingHookSchema = z.object({
  expectedElement: z.string(),
  whyExpected: z.string(),
  personaAttribute: z.string(),
  predictedImpact: z.enum(['low', 'medium', 'high']),
  priorityRank: z.number(),
});

export const TriggerPhraseSchema = z.object({
  phrase: z.string(),
  why: z.string(),
  personaAttribute: z.string(),
});

export const WastedPhraseSchema = z.object({
  phrase: z.string(),
  why: z.string(),
});

export const TriggerLanguageSchema = z.object({
  positive: z.array(TriggerPhraseSchema),
  negative: z.array(TriggerPhraseSchema),
  wasted: z.array(WastedPhraseSchema),
  missingVocabulary: z.array(z.string()),
});

export const AttentionFlowSchema = z.object({
  hookStrength: ScoreSchema,
  dropOffPoint: z.string().nullable(),
  reEngagementMoments: z.array(z.string()),
  cognitiveLoadFlags: z.array(z.string()),
});

export const CredibilityErosionMomentSchema = z.object({
  quote: z.string(),
  reason: z.string(),
});

export const TrustSignalsSchema = z.object({
  evidenceTypeMatch: z.boolean(),
  evidenceTypesPresent: z.array(EvidenceTypeSchema),
  evidenceTypesPreferred: z.array(EvidenceTypeSchema),
  credibilityErosionMoments: z.array(CredibilityErosionMomentSchema),
});

export const ToneFitDetailSchema = z.object({
  detectedTone: ToneSchema,
  preferredTone: ToneSchema,
  perceivedRepPosture: z.enum(['peer', 'vendor', 'educator', 'pest']),
  patientCentricity: ScoreSchema,
});

export const BehavioralPredictionsSchema = z.object({
  immediateReaction: z.enum(['engage', 'politely_dismiss', 'redirect', 'clarifying_question']),
  likelyFollowupQuestion: z.string(),
  infoTheyWouldVerify: z.array(z.string()),
  wordOfMouthProbability: ScoreSchema,
  behaviorChangeHorizon: BehaviorHorizonSchema,
});

export const CompetitiveFramingSchema = z.object({
  implicitComparator: z.string().nullable(),
  switchingCostPerception: z.string(),
  currentStateAcknowledgment: z.boolean(),
});

export const CategoryFitDetailSchema = z.object({
  categoryPriorityForPersona: ScoreSchema,
  suggestedBetterCategory: z.string().nullable(),
  categoryExpectationGaps: z.array(z.string()),
});

export const AttributeConflictSchema = z.object({
  a: z.string(),
  b: z.string(),
  resolution: z.string(),
});

export const AttributeUtilizationSchema = z.object({
  invoked: z.array(z.string()),
  unused: z.array(z.string()),
  conflicts: z.array(AttributeConflictSchema),
});

export const ComplianceFlagSchema = z.object({
  type: z.enum(['off_label_hint', 'unbalanced_claim', 'missing_isi_cue', 'unqualified_superlative', 'other']),
  severity: SeveritySchema,
  quote: z.string(),
  rationale: z.string(),
});

export const ImprovementSuggestionSchema = z.object({
  change: z.string(),
  rationale: z.string(),
  predictedScoreLift: z.enum(['low', 'medium', 'high']),
  scoresAffected: z.array(z.string()),
});

export const OverallAssessmentSchema = z.object({
  verdict: z.enum(['strong_fit', 'workable', 'weak_fit', 'do_not_use']),
  oneLineSummary: z.string(),
  topStrength: z.string(),
  topWeakness: z.string(),
});

export const MessageTestingScoresSchema = z.object({
  personaResonance: ScoreSchema,
  objectiveAlignment: ScoreSchema,
  categoryFit: ScoreSchema,
  predictedEngagement: ScoreSchema,
  predictedActionLikelihood: ScoreSchema,
  clarity: ScoreSchema,
  credibility: ScoreSchema,
  cognitiveLoad: ScoreSchema,
  differentiation: ScoreSchema,
  ctaStrength: ScoreSchema,
  toneFit: ScoreSchema,
});

// ─── Sub-Judge Output Schemas ───────────────────────────────────────────────

export const ScoresJudgeOutputSchema = z.object({
  scores: MessageTestingScoresSchema,
  attentionFlow: AttentionFlowSchema,
  trustSignals: TrustSignalsSchema,
  toneFitDetail: ToneFitDetailSchema,
  categoryFitDetail: CategoryFitDetailSchema,
  attributeUtilization: AttributeUtilizationSchema,
  improvementSuggestions: z.array(ImprovementSuggestionSchema),
  overallAssessment: OverallAssessmentSchema,
});
export type ScoresJudgeOutput = z.infer<typeof ScoresJudgeOutputSchema>;

export const ObjectionsJudgeOutputSchema = z.object({
  objections: z.array(ObjectionSchema),
  missingHooks: z.array(MissingHookSchema),
  competitiveFraming: CompetitiveFramingSchema,
});
export type ObjectionsJudgeOutput = z.infer<typeof ObjectionsJudgeOutputSchema>;

export const BehaviorJudgeOutputSchema = z.object({
  behavioralPredictions: BehavioralPredictionsSchema,
});
export type BehaviorJudgeOutput = z.infer<typeof BehaviorJudgeOutputSchema>;

export const LanguageJudgeOutputSchema = z.object({
  triggerLanguage: TriggerLanguageSchema,
});
export type LanguageJudgeOutput = z.infer<typeof LanguageJudgeOutputSchema>;

// ─── Composite Cell ─────────────────────────────────────────────────────────

export const MessageTestingCellSchema = z.object({
  messageId: z.string(),
  segmentId: z.string(),
  generatedAt: z.string(),

  scores: MessageTestingScoresSchema,

  objections: z.array(ObjectionSchema),
  missingHooks: z.array(MissingHookSchema),
  triggerLanguage: TriggerLanguageSchema,
  attentionFlow: AttentionFlowSchema,
  trustSignals: TrustSignalsSchema,
  toneFitDetail: ToneFitDetailSchema,
  behavioralPredictions: BehavioralPredictionsSchema,
  competitiveFraming: CompetitiveFramingSchema,
  categoryFitDetail: CategoryFitDetailSchema,
  attributeUtilization: AttributeUtilizationSchema,
  improvementSuggestions: z.array(ImprovementSuggestionSchema),
  overallAssessment: OverallAssessmentSchema,
});
export type MessageTestingCell = z.infer<typeof MessageTestingCellSchema>;

// ─── Result Envelope ────────────────────────────────────────────────────────

export const MessageTestingProgressSchema = z.object({
  runIdentifier: z.string(),
  completed: z.number(),
  total: z.number(),
  latestCell: MessageTestingCellSchema.optional(),
});
export type MessageTestingProgress = z.infer<typeof MessageTestingProgressSchema>;

export const MessageTestingResultSchema = z.object({
  runIdentifier: z.string(),
  status: z.enum(['processing', 'completed', 'failed']),
  matrix: z.array(MessageTestingCellSchema),
  progress: z.object({
    completed: z.number(),
    total: z.number(),
  }),
});
export type MessageTestingResult = z.infer<typeof MessageTestingResultSchema>;
